import React, {useContext, useState} from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, InputAdornment, MenuItem, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, Delete02Icon, EyeIcon, EyeOffIcon, PencilEdit01Icon} from "@hugeicons/core-free-icons";
import {WebSettingsContext} from "../../context/WebSettingsContext";
import {SignInContext} from "../../context/SignInContext";
import useConfirm from "../../hooks/useConfirm";
import SearchInput from "../../components/inputs/SearchInput";

const PROVIDERS = ["OpenAI", "Anthropic", "Google", "Meta", "Mistral", "Custom"];

const EMPTY_MODEL = {
  name: "",
  provider: "OpenAI",
  modelId: "",
  description: "",
  apiKey: "",
  baseUrl: "",
  organizationId: null,
};

export const AiModels = () => {
  const {settings, onSaveModel, onDeleteModel} = useContext(WebSettingsContext);
  const {organizations} = useContext(SignInContext);
  const [openModal, setOpenModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [search, setSearch] = useState("");

  const confirmDelete = useConfirm("Are you sure you want to delete this model?");

  const models = settings?.models || [];

  const getOrgName = (orgId) => {
    if (!orgId) return "Public";
    return organizations?.find(o => o.id === orgId)?.name || orgId;
  };

  const query = search.trim().toLowerCase();
  const filteredModels = query
    ? models.filter(model => [model.name, model.provider, model.modelId, model.description, getOrgName(model.organizationId)]
      .some(value => String(value || "").toLowerCase().includes(query)))
    : models;

  const handleDelete = async (model) => {
    const confirmed = await confirmDelete({title: `Delete "${model.name}"?`});
    if (confirmed) {
      await onDeleteModel(model.id);
    }
  };

  return (
    <>
      <ModelDialog
        open={openModal}
        selectedModel={selectedModel}
        handleClose={() => {
          setOpenModal(false);
          setSelectedModel(null);
        }}
        organizations={organizations}
      />
      <Grid maxWidth={"lg"} sx={{mx: "auto", p: 3, my: 5, borderRadius: 4, bgcolor: "#fefefeee"}} container className={"border-shadow"}>
        <Grid item xs={12}>
          <Stack direction={{xs: "column", md: "row"}} gap={2} alignItems={{xs: "stretch", md: "center"}}>
            <Box sx={{flexShrink: 0}}>
              <Typography variant={"h4"} fontWeight={600}>AI Models</Typography>
              <Typography variant={"body2"} color={"text.secondary"} mt={0.5}>
                Manage available LLM models across the platform
              </Typography>
            </Box>
            <SearchInput value={search} onChange={setSearch} placeholder={"Search models..."}/>
            <Button
              sx={{textWrap: "nowrap", flexShrink: 0}} variant={"primary"}
              startIcon={<HugeiconsIcon icon={Add01Icon} size={18}/>}
              onClick={() => {
                setSelectedModel(null);
                setOpenModal(true);
              }}
            >
              Add Model
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{fontWeight: 700}}>Name</TableCell>
                <TableCell sx={{fontWeight: 700}}>Provider</TableCell>
                <TableCell sx={{fontWeight: 700}}>Model ID</TableCell>
                <TableCell sx={{fontWeight: 700}}>Description</TableCell>
                <TableCell sx={{fontWeight: 700}}>Access</TableCell>
                <TableCell sx={{fontWeight: 700}}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredModels.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align={"center"} sx={{py: 4, color: "text.secondary"}}>
                    {models.length === 0 ? "No models added yet" : `No models match "${search}"`}
                  </TableCell>
                </TableRow>
              )}
              {filteredModels.map(model => (
                <TableRow key={model.id}>
                  <TableCell>
                    <Typography fontWeight={500}>{model.name}</Typography>
                  </TableCell>
                  <TableCell>{model.provider}</TableCell>
                  <TableCell>
                    <Typography variant={"body2"} fontFamily={"monospace"} fontSize={13}>
                      {model.modelId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={model.description || ""}>
                      <Typography variant={"body2"} noWrap sx={{maxWidth: 220}}>
                        {model.description || "—"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant={"body2"} color={model.organizationId ? "text.primary" : "text.secondary"}>
                      {getOrgName(model.organizationId)}
                    </Typography>
                  </TableCell>
                  <TableCell padding={"checkbox"}>
                    <Stack direction={"row"} gap={1} alignItems={"center"} sx={{px: 1}}>
                      <IconButton
                        sx={{borderRadius: "50% !important"}}
                        onClick={() => {
                          setSelectedModel(model);
                          setOpenModal(true);
                        }}
                      >
                        <HugeiconsIcon icon={PencilEdit01Icon} size={18}/>
                      </IconButton>
                      <IconButton
                        color={"error"}
                        sx={{borderRadius: "50% !important"}}
                        onClick={() => handleDelete(model)}
                      >
                        <HugeiconsIcon icon={Delete02Icon} size={18}/>
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>
      </Grid>
    </>
  );
};

const ModelDialog = ({open, handleClose, selectedModel, organizations}) => {
  const {onSaveModel} = useContext(WebSettingsContext);
  const [model, setModel] = useState(EMPTY_MODEL);
  const [showApiKey, setShowApiKey] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    setShowApiKey(false);
    setModel(selectedModel
      ? {
          id: selectedModel.id,
          name: selectedModel.name || "",
          provider: selectedModel.provider || "OpenAI",
          modelId: selectedModel.modelId || "",
          description: selectedModel.description || "",
          apiKey: selectedModel.apiKey || "",
          baseUrl: selectedModel.baseUrl || "",
          organizationId: selectedModel.organizationId || null,
        }
      : EMPTY_MODEL
    );
  }, [selectedModel, open]);

  const onChange = (e) => {
    const {name, value} = e.target;
    setModel(prev => ({...prev, [name]: value}));
  };

  const isCustom = model.provider === "Custom";
  const isEdit = Boolean(selectedModel?.id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!model.name.trim() || !model.modelId.trim()) return;
    setLoading(true);
    const success = await onSaveModel(model);
    setLoading(false);
    if (success !== false) handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit,
        sx: {minWidth: "360px", maxWidth: "sm", borderRadius: 4, p: 2, width: "100%"},
      }}
    >
      <DialogTitle>{isEdit ? "Edit" : "Add"} AI Model</DialogTitle>
      <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2, py: "16px !important"}}>
        <TextField
          label={"Display Name"}
          name={"name"}
          required
          fullWidth
          value={model.name}
          onChange={onChange}
          placeholder={"e.g. GPT-4o"}
        />
        <TextField
          label={"Provider"}
          name={"provider"}
          required
          fullWidth
          select
          value={model.provider}
          onChange={onChange}
        >
          {PROVIDERS.map(p => (
            <MenuItem key={p} value={p}>{p}</MenuItem>
          ))}
        </TextField>
        <TextField
          label={"Model ID"}
          name={"modelId"}
          required
          fullWidth
          value={model.modelId}
          onChange={onChange}
          placeholder={"e.g. gpt-4o"}
          helperText={"Exact identifier used in API calls"}
          disabled={isEdit}
        />

        {isCustom ? (
          <TextField
            label={"Base URL"}
            name={"baseUrl"}
            fullWidth
            value={model.baseUrl}
            onChange={onChange}
            placeholder={"https://your-endpoint.com/v1"}
          />
        ) : (
          <TextField
            label={"API Key"}
            name={"apiKey"}
            fullWidth
            type={showApiKey ? "text" : "password"}
            value={model.apiKey}
            onChange={onChange}
            placeholder={"sk-..."}
            InputProps={{
              endAdornment: (
                <InputAdornment position={"end"}>
                  <IconButton size={"small"} onClick={() => setShowApiKey(prev => !prev)} edge={"end"}>
                    {showApiKey ? <HugeiconsIcon icon={EyeOffIcon} size={18}/> : <HugeiconsIcon icon={EyeIcon} size={18}/>}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        )}

        <TextField
          label={"Description"}
          name={"description"}
          fullWidth
          multiline
          minRows={2}
          value={model.description}
          onChange={onChange}
          placeholder={"Short description of this model's capabilities"}
        />

        <TextField
          label={"Organization Access"}
          name={"organizationId"}
          fullWidth
          select
          value={model.organizationId || ""}
          onChange={(e) => setModel(prev => ({...prev, organizationId: e.target.value || null}))}
          helperText={"Leave empty to make this model available to all users"}
        >
          <MenuItem value={""}>Public (all users)</MenuItem>
          {organizations?.map(org => (
            <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions sx={{px: 2, py: 1}}>
        <Button variant="ghost" onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          type={"submit"}
          variant={"modal"}
          disabled={loading}
        >
          {isEdit ? "Update" : "Add Model"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
